import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';

import { Typography } from '@zendesk/forethought-elements';
import { getAbTestDateRange } from '../ab-test-config/utils';
import { CARD_HEIGHT, CARD_WIDTH, LINE_CHART_HEIGHT } from './constants';
import LoadingCard from './LoadingCard';
import RecommendationCard from './RecommendationCard';
import WinnerCard from './WinnerCard';
import partition from 'lodash/fp/partition';
import ErrorState from 'src/components/dashboard-chart-section/ErrorState';
import Chart from 'src/components/dashboard-pages/solve-insights/cards/Chart';
import { dateRangeToTimestamp } from 'src/components/dashboard-pages/solve-insights/helpers';
import {
  useGetAbTestConfigByIdQuery,
  useGetAbTestResultChartsQuery,
} from 'src/services/agent-ab-test/agentAbTestApi';
import { CustomEventChart } from 'src/services/insight-cards/types';

const ResponsiveGridWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box
      sx={{
        '& > *': {
          flex: '1 1 100%',
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix -- order matters here
          '@media (min-width: 1080px)': {
            flex: '1 1 calc(50% - 12px)',
            maxWidth: 'calc(50% - 12px)',
            minWidth: 'calc(50% - 12px)',
          },
          '@media (min-width: 1680px)': {
            flex: '1 1 calc(33.333% - 16px)',
            maxWidth: 'calc(33.333% - 16px)',
            minWidth: 'calc(33.333% - 16px)',
          },
        },
        display: 'flex',
        flexWrap: 'wrap',
        gap: '24px',
      }}
    >
      {children}
    </Box>
  );
};

const AbTestResultsPage = () => {
  const { palette } = useTheme();
  const { abTestId = '' } = useParams();
  const { t } = useTranslation('agent-ab-test');

  const {
    data: charts = [],
    isError,
    isLoading,
  } = useGetAbTestResultChartsQuery(abTestId, { skip: !abTestId });

  const { data: abTestConfig } = useGetAbTestConfigByIdQuery(abTestId, {
    skip: !abTestId,
  });

  const { endTimestamp, startTimestamp } = useMemo(() => {
    const dateRange = getAbTestDateRange(
      abTestConfig?.start_datetime,
      abTestConfig?.end_condition?.end_datetime,
      abTestConfig?.timezone,
    );
    if (!dateRange) {
      return {};
    }
    const { end_timestamp, start_timestamp } = dateRangeToTimestamp(dateRange);
    return { endTimestamp: end_timestamp, startTimestamp: start_timestamp };
  }, [
    abTestConfig?.start_datetime,
    abTestConfig?.end_condition?.end_datetime,
    abTestConfig?.timezone,
  ]);

  // Winner metric_ids (e.g. "deflection", "csat") appear as substrings of
  // result chart_ids (e.g. "ab_test_results_deflection_rate"). This convention
  // is maintained by the backend chart registration.
  const winnerMetricIds = useMemo(
    () => (abTestConfig?.winner_metrics ?? []).map(m => m.metric_id),
    [abTestConfig?.winner_metrics],
  );

  const isPrimaryChart = (chart: CustomEventChart) =>
    winnerMetricIds.some(metricId => chart.chart_id.includes(metricId));

  const [lineCharts, smallCharts] = useMemo(() => {
    return partition(chart => chart.chart_type === 'line', charts);
  }, [charts]);

  if (isLoading) {
    return (
      <Box
        sx={{
          bgcolor: palette.colors.white,
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          padding: '40px',
        }}
      >
        <ResponsiveGridWrapper>
          {Array.from({ length: 6 }).map((_, i) => (
            <LoadingCard key={i} />
          ))}
        </ResponsiveGridWrapper>
      </Box>
    );
  }

  if (isError) {
    return <ErrorState />;
  }

  if (!charts?.length) {
    return (
      <Box
        alignItems='center'
        bgcolor={palette.colors.white}
        display='flex'
        justifyContent='center'
        p='80px'
      >
        <Typography color={palette.colors.grey[600]} variant='font16'>
          {t('resultsPage.noResults')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: palette.colors.white,
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        padding: '40px',
      }}
    >
      {/* Top row: Winner + Recommendation side by side */}
      <Box
        sx={{
          display: 'flex',
          gap: '24px',
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix -- order matters here
          '@media (max-width: 1080px)': {
            flexDirection: 'column',
          },
        }}
      >
        <Box sx={{ flex: '0 0 calc(33% - 12px)', minWidth: 0 }}>
          <WinnerCard />
        </Box>
        <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
          <RecommendationCard status={abTestConfig?.status} />
        </Box>
      </Box>
      {/* Charts grid */}
      <ResponsiveGridWrapper>
        {smallCharts.length > 0 &&
          smallCharts.map(chart => {
            return (
              <Box
                border={`1px solid ${palette.colors.slate[200]}`}
                borderRadius='8px'
                display='flex'
                flexDirection='column'
                height={CARD_HEIGHT}
                key={chart.chart_id}
                minWidth={CARD_WIDTH}
                p='14px'
              >
                <Chart
                  channels={chart.channels}
                  chart={chart}
                  endTimestamp={endTimestamp}
                  isPrimary={isPrimaryChart(chart)}
                  shouldUseChatsTableRequestParams={false}
                  showEmptyState
                  startTimestamp={startTimestamp}
                />
              </Box>
            );
          })}
        {lineCharts.map(chart => (
          <Box
            border={`1px solid ${palette.colors.slate[200]}`}
            borderRadius='8px'
            display='flex'
            flexBasis='100%'
            flexDirection='column'
            height={LINE_CHART_HEIGHT}
            key={chart.chart_id}
            maxWidth='100%'
            p='14px'
          >
            <Chart
              channels={chart.channels}
              chart={chart}
              endTimestamp={endTimestamp}
              isPrimary={isPrimaryChart(chart)}
              shouldUseChatsTableRequestParams={false}
              showEmptyState
              startTimestamp={startTimestamp}
            />
          </Box>
        ))}
      </ResponsiveGridWrapper>
    </Box>
  );
};

export default AbTestResultsPage;
